import {
    Component,
    EventEmitter,
    Input,
    Output,
    type OnInit,
} from "@angular/core";
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from "@angular/forms";
import { RequireMatch } from "app/validators/requireMatch";
import { Filter, PageOptions } from "cw-backend";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";
import { BondService } from "../../../../../../../../../../shared/cw-backend/providers/module-financial/bonds/bond.service";

@Component({
    selector: "cw-search-entity-type",
    templateUrl: "./search-entity-type.component.html",
    styleUrls: ["./search-entity-type.component.scss"],
})
export class SearchEntityTypeComponent implements OnInit {
    @Input() scope!: string;
    @Output() oEmitValue: EventEmitter<any> = new EventEmitter();

    formEntityType!: FormGroup;

    typeDebtorControl = new FormControl();

    //List
    companyList: any[] = [];
    companyTargetList: any[] = [];
    companyAccountPlanList: any[] = [];
    performerList: any[] = [];
    professionalList: any[] = [];
    personList: any[] = [];
    firmList: any[] = [];
    supplierList: any[] = [];
    insuranceList: any[] = [];
    patientList: any[] = [];
    userList: any[] = [];

    // Page options
    companyPageOptions: PageOptions = new PageOptions();
    companyTargetPageOptions: PageOptions = new PageOptions();
    companyAccountPlanPageOptions: PageOptions = new PageOptions();
    supplierPageOptions: PageOptions = new PageOptions();
    insurancePageOptions: PageOptions = new PageOptions();
    firmPageOptions: PageOptions = new PageOptions();
    patientPageOptions: PageOptions = new PageOptions();
    performerPageOptions: PageOptions = new PageOptions();
    personPageOptions: PageOptions = new PageOptions();
    professionalPageOptions: PageOptions = new PageOptions();
    userPageOptions: PageOptions = new PageOptions();
    docTypePageOptions: PageOptions = new PageOptions();

    typeDebtorList = [
        { name: "Convênio", id: "C" },
        { name: "Empresa", id: "E" },
        { name: "Firma", id: "F" },
        { name: "Fornecedor", id: "O" },
        { name: "Paciente", id: "P" },
        { name: "Pessoa Física", id: "S" },
        { name: "Profissional Executante", id: "X" },
        { name: "Profissional Solicitante", id: "R" },
        { name: "Usuário", id: "U" },
    ];

    filter = new Filter();

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _fb: FormBuilder,
        private bondProvider: BondService,
    ) {
        this.patientPageOptions.fieldSort = "person.name";
        this.performerPageOptions.fieldSort = "professional.person.name";
        this.professionalPageOptions.fieldSort = "person.name";
        this.userPageOptions.fieldSort = "person.name";
        this.companyAccountPlanPageOptions.fieldSort = "accountPlan.name";

        this.filter.key = "GENERIC_SEARCH";
        this.filter.alias = "Busca generica";
    }

    ngOnInit(): void {
        this.formEntityType = this.createForm();
        this.observableTypeDebtor();
        this.initFilter();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(true);
        this._unsubscribeAll.complete();
    }

    createForm() {
        return this._fb.group({
            firm: [null],
            person: [null],
            companyTarget: [null],
            supplier: [null],
            performer: [null],
            professional: [null],
            insurance: [null],
            patient: [null],
            user: [null],
        });
    }

    observableTypeDebtor() {
        this.typeDebtorControl.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (res: any) => {
                    this.clearValidators();
                    this.filter.type = res?.id ? res.id : null;

                    switch (res?.id) {
                        case "C":
                            this.formEntityType.controls[
                                "insurance"
                            ].setValidators([
                                Validators.required,
                                RequireMatch,
                            ]);
                            this.formEntityType.controls[
                                "insurance"
                            ].updateValueAndValidity();
                            break;
                        case "E":
                            this.formEntityType.controls[
                                "companyTarget"
                            ].setValidators([
                                Validators.required,
                                RequireMatch,
                            ]);
                            this.formEntityType.controls[
                                "companyTarget"
                            ].updateValueAndValidity();
                            break;
                        case "F":
                            this.formEntityType.controls["firm"].setValidators([
                                Validators.required,
                                RequireMatch,
                            ]);
                            this.formEntityType.controls[
                                "firm"
                            ].updateValueAndValidity();
                            break;
                        case "O":
                            this.formEntityType.controls[
                                "supplier"
                            ].setValidators([
                                Validators.required,
                                RequireMatch,
                            ]);
                            this.formEntityType.controls[
                                "supplier"
                            ].updateValueAndValidity();
                            break;
                        case "P":
                            this.formEntityType.controls[
                                "patient"
                            ].setValidators([
                                Validators.required,
                                RequireMatch,
                            ]);
                            this.formEntityType.controls[
                                "patient"
                            ].updateValueAndValidity();
                            break;
                        case "S":
                            this.formEntityType.controls[
                                "person"
                            ].setValidators([
                                Validators.required,
                                RequireMatch,
                            ]);
                            this.formEntityType.controls[
                                "person"
                            ].updateValueAndValidity();
                            break;
                        case "X":
                            this.formEntityType.controls[
                                "performer"
                            ].setValidators([
                                Validators.required,
                                RequireMatch,
                            ]);
                            this.formEntityType.controls[
                                "performer"
                            ].updateValueAndValidity();
                            break;
                        case "R":
                            this.formEntityType.controls[
                                "professional"
                            ].setValidators([
                                Validators.required,
                                RequireMatch,
                            ]);
                            this.formEntityType.controls[
                                "professional"
                            ].updateValueAndValidity();
                            break;
                        case "U":
                            this.formEntityType.controls["user"].setValidators([
                                Validators.required,
                                RequireMatch,
                            ]);
                            this.formEntityType.controls[
                                "user"
                            ].updateValueAndValidity();
                            break;
                        default:
                            break;
                    }
                },
            });
    }

    initFilter() {
        const registerFilter = (
            controlName: string,
            pageOptions: { argument: string },
            fetchFn: (value: any) => void,
        ) => {
            this.formEntityType
                .get(controlName)
                ?.valueChanges.pipe(
                    takeUntil(this._unsubscribeAll),
                    debounceTime(500),
                    distinctUntilChanged(),
                )
                .subscribe((value: any) => {
                    const filter = typeof value === "string" ? value : "";
                    pageOptions.argument = filter;
                    fetchFn(value);
                });
        };

        registerFilter(
            "companyTarget",
            this.companyTargetPageOptions,
            (companyTarget: any) => {
                this.getPageCompanyTarget();
                if (companyTarget?.id) {
                    this.filter.value = companyTarget.id;
                } else {
                    this.filter.value = "";
                }
                this.emitValue();
            },
        );

        registerFilter(
            "insurance",
            this.insurancePageOptions,
            (insurance: any) => {
                this.getPageInsurance();
                if (insurance?.id) {
                    this.filter.value = insurance.id;
                } else {
                    this.filter.value = "";
                }
                this.emitValue();
            },
        );

        registerFilter("performer", this.performerPageOptions, (performer) => {
            this.getPagePerformer();
            if (performer?.professionalId) {
                this.filter.value = performer.professionalId;
            } else {
                this.filter.value = "";
            }
            this.emitValue();
        });

        registerFilter("firm", this.firmPageOptions, (firm) => {
            this.getPageFirm();
            if (firm?.id) {
                this.filter.value = firm.id;
            } else {
                this.filter.value = "";
            }
            this.emitValue();
        });

        registerFilter("supplier", this.supplierPageOptions, (supplier) => {
            this.getPageSupplier();
            if (supplier?.id) {
                this.filter.value = supplier.id;
            } else {
                this.filter.value = "";
            }
            this.emitValue();
        });

        registerFilter("patient", this.patientPageOptions, (patient) => {
            this.getPagePatient();
            if (patient?.id) {
                this.filter.value = patient.id;
            } else {
                this.filter.value = "";
            }
            this.emitValue();
        });

        registerFilter("person", this.personPageOptions, (person) => {
            this.getPagePerson();
            if (person?.personId) {
                this.filter.value = person.personId;
            } else {
                this.filter.value = "";
            }
            this.emitValue();
        });

        registerFilter(
            "professional",
            this.professionalPageOptions,
            (professional) => {
                this.getPageProfessional();
                if (professional?.id) {
                    this.filter.value = professional.id;
                } else {
                    this.filter.value = "";
                }
                this.emitValue();
            },
        );

        registerFilter("user", this.userPageOptions, (user) => {
            this.getPageUser();
            if (user?.id) {
                this.filter.value = user.id;
            } else {
                this.filter.value = "";
            }
            this.emitValue();
        });
    }

    getPageSupplier() {
        this.bondProvider
            .getPageSupplier(this.supplierPageOptions)
            .then((res: any) => {
                this.supplierList = res.body.content;
            });
    }

    getPagePatient() {
        this.bondProvider
            .getPagePatient(this.patientPageOptions)
            .then((res: any) => {
                this.patientList = res.body.content;
            });
    }

    getPagePerson() {
        this.bondProvider
            .getPagePerson(this.personPageOptions)
            .then((res: any) => {
                this.personList = res.body.content;
            });
    }

    getPageCompany() {
        this.bondProvider
            .getPageCompany(this.companyPageOptions)
            .then((res: any) => {
                this.companyList = res.body.content;
            });
    }

    getPageCompanyTarget() {
        this.bondProvider
            .getPageCompany(this.companyTargetPageOptions)
            .then((res: any) => {
                this.companyTargetList = res.body.content;
            });
    }

    getPageInsurance() {
        this.bondProvider
            .getPageInsurance(this.insurancePageOptions)
            .then((res: any) => {
                this.insuranceList = res.body.content;
            });
    }

    getPagePerformer() {
        this.bondProvider
            .getPagePerformer(this.performerPageOptions)
            .then((res: any) => {
                this.performerList = res.body.content;
            });
    }

    getPageFirm() {
        this.bondProvider.getPageFirm(this.firmPageOptions).then((res: any) => {
            this.firmList = res.body.content;
        });
    }

    getPageProfessional() {
        this.bondProvider
            .getPageProfessional(this.professionalPageOptions)
            .then((res: any) => {
                this.professionalList = res.body.content;
            });
    }

    getPageUser() {
        this.bondProvider.getPageUser(this.userPageOptions).then((res: any) => {
            this.userList = res.body.content;
        });
    }

    clearValidators() {
        this.formEntityType.controls["insurance"].clearValidators();
        this.formEntityType.controls["insurance"].updateValueAndValidity({
            emitEvent: false,
        });
        this.formEntityType.controls["companyTarget"].removeValidators(
            Validators.required,
        );
        this.formEntityType.controls["companyTarget"].updateValueAndValidity({
            emitEvent: false,
        });
        this.formEntityType.controls["firm"].clearValidators();
        this.formEntityType.controls["firm"].updateValueAndValidity({
            emitEvent: false,
        });
        this.formEntityType.controls["supplier"].clearValidators();
        this.formEntityType.controls["supplier"].updateValueAndValidity({
            emitEvent: false,
        });
        this.formEntityType.controls["patient"].clearValidators();
        this.formEntityType.controls["patient"].updateValueAndValidity({
            emitEvent: false,
        });
        this.formEntityType.controls["person"].clearValidators();
        this.formEntityType.controls["person"].updateValueAndValidity({
            emitEvent: false,
        });
        this.formEntityType.controls["performer"].removeValidators(
            Validators.required,
        );
        this.formEntityType.controls["performer"].updateValueAndValidity({
            emitEvent: false,
        });
        this.formEntityType.controls["professional"].clearValidators();
        this.formEntityType.controls["professional"].updateValueAndValidity({
            emitEvent: false,
        });
        this.formEntityType.controls["user"].clearValidators();
        this.formEntityType.controls["user"].updateValueAndValidity({
            emitEvent: false,
        });
        this.formEntityType.controls["insurance"].setValue(null, {
            emitEvent: false,
        });
        this.formEntityType.controls["companyTarget"].setValue(null, {
            emitEvent: false,
        });
        this.formEntityType.controls["firm"].setValue(null, {
            emitEvent: false,
        });
        this.formEntityType.controls["supplier"].setValue(null, {
            emitEvent: false,
        });
        this.formEntityType.controls["patient"].setValue(null, {
            emitEvent: false,
        });
        this.formEntityType.controls["person"].setValue(null, {
            emitEvent: false,
        });
        this.formEntityType.controls["performer"].setValue(null, {
            emitEvent: false,
        });
        this.formEntityType.controls["professional"].setValue(null, {
            emitEvent: false,
        });
        this.formEntityType.controls["user"].setValue(null, {
            emitEvent: false,
        });
        this.formEntityType.controls["user"].setValue(null, {
            emitEvent: false,
        });
    }

    emitValue() {
        this.oEmitValue.emit(this.filter);
    }

    // Compare object in select
    compareSelect(o1: any, o2: any): boolean {
        if (!o1 || !o2) {
            return false;
        }
        return o1.id === o2.id;
    }

    compareEnum(o1: any, o2: any): boolean {
        if (o1 == null || o2 == null) {
            return false;
        }
        return o1 === o2;
    }

    displayFn(obj?: any): string | undefined | null {
        if (obj?.name) {
            return obj.name;
        } else if (obj?.performerName) {
            return obj.performerName;
        }
        return null;
    }

    displayFnCompanyPlan(obj?: any): string | undefined | null {
        if (obj?.accountPlanName) {
            return obj.accountPlanName;
        }
        return null;
    }

    displayFnPatientPlan(obj?: any): string | undefined | null {
        if (obj?.patientName) {
            return obj.patientName;
        }
        return null;
    }

    displayFnCompany(obj?: any): string | undefined | null {
        if (obj?.alias) {
            return obj.alias;
        }
        return null;
    }
}
